import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import axiosInstance from '@/lib/enhancedAxiosInstance';
import { logError, logInfo } from '@/utils/errorHandler';

// Session configuration constants
const IDLE_TIMEOUT = 60 * 60 * 1000; // 60 minutes in milliseconds
const ABSOLUTE_SESSION_CAP = 8 * 60 * 60 * 1000; // 8 hours in milliseconds
const WARNING_BEFORE_EXPIRY = 5 * 60 * 1000; // 5 minutes in milliseconds
const SILENT_REFRESH_BEFORE_EXPIRY = 5 * 60 * 1000; // 5 minutes before expiry
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

interface SessionState {
  isActive: boolean;
  lastActivity: number;
  sessionStart: number;
  showWarning: boolean;
  isRefreshing: boolean;
  absoluteExpiry: number;
}

interface SessionManagerHook {
  sessionState: SessionState;
  extendSession: () => void;
  logout: () => void;
  isSessionExpired: boolean;
  timeUntilIdleExpiry: number;
  timeUntilAbsoluteExpiry: number;
}

export function useSessionManager(): SessionManagerHook {
  const [sessionState, setSessionState] = useState<SessionState>(() => {
    const now = Date.now();
    const sessionStart = parseInt(localStorage.getItem('session_start') || now.toString());

    return {
      isActive: true,
      lastActivity: now,
      sessionStart,
      showWarning: false,
      isRefreshing: false,
      absoluteExpiry: sessionStart + ABSOLUTE_SESSION_CAP,
    };
  });

  const broadcastChannel = useRef<BroadcastChannel | null>(null);
  const timers = useRef<{
    warningTimer?: NodeJS.Timeout;
    idleTimer?: NodeJS.Timeout;
    absoluteTimer?: NodeJS.Timeout;
    silentRefreshTimer?: NodeJS.Timeout;
  }>({});

  // Broadcast session events to other tabs
  const broadcastEvent = useCallback((type: string, data?: any) => {
    if (broadcastChannel.current) {
      broadcastChannel.current.postMessage({ type, data });
    }
  }, []);

  // Handle logout
  const handleLogout = useCallback(
    (broadcast = true) => {
      // Clear all session data
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('session_start');

      // Clear timers
      Object.values(timers.current).forEach(timer => {
        if (timer) clearTimeout(timer);
      });

      if (broadcast) {
        broadcastEvent('SESSION_LOGOUT');
      }

      // Redirect to login
      window.location.href = '/login';
    },
    [broadcastEvent]
  );

  // Handle session expiry
  const handleSessionExpiry = useCallback(
    (reason: 'idle' | 'absolute' | 'refresh_failed' | 'no_refresh_token') => {
      logInfo('Session expired', {
        component: 'useSessionManager',
        action: 'handleSessionExpiry',
        metadata: { reason },
      });

      // Clear all timers
      Object.values(timers.current).forEach(timer => {
        if (timer) clearTimeout(timer);
      });

      // Show appropriate expiry message
      const messages = {
        idle: 'Your session has expired due to inactivity. Please log in again.',
        absolute: 'Your session has reached the maximum duration. Please log in again.',
        refresh_failed: 'Your session has expired. Please log in again.',
        no_refresh_token: 'Your session has expired. Please log in again.',
      };

      // Use a more prominent notification for session expiry
      toast.error(messages[reason], {
        duration: 10000,
        description: 'You will be redirected to the login page.',
        action: {
          label: 'Login Now',
          onClick: () => handleLogout(),
        },
      });

      // Delay logout to allow user to see the message
      setTimeout(() => {
        handleLogout();
      }, 2000);
    },
    [handleLogout]
  );

  // Perform silent token refresh
  const performSilentRefresh = useCallback(async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      handleSessionExpiry('no_refresh_token');
      return;
    }

    setSessionState(prev => ({ ...prev, isRefreshing: true }));

    try {
      const response = await axiosInstance.post('/api/token/refresh/', {
        refresh: refreshToken,
      });

      if (response.data.access) {
        localStorage.setItem('access_token', response.data.access);

        // Update refresh token if provided (token rotation)
        if (response.data.refresh) {
          localStorage.setItem('refresh_token', response.data.refresh);
        }

        broadcastEvent('SESSION_REFRESH');

        logInfo('Silent token refresh successful', {
          component: 'useSessionManager',
          action: 'performSilentRefresh',
        });

        // Reset activity timer after successful refresh
        const now = Date.now();
        setSessionState(prev => ({
          ...prev,
          lastActivity: now,
          showWarning: false,
          isRefreshing: false,
        }));

        broadcastEvent('SESSION_ACTIVITY', { timestamp: now });
      }
    } catch (error) {
      logError('Silent token refresh failed', {
        component: 'useSessionManager',
        action: 'performSilentRefresh',
        metadata: { error },
      });

      handleSessionExpiry('refresh_failed');
    } finally {
      setSessionState(prev => ({ ...prev, isRefreshing: false }));
    }
  }, [handleSessionExpiry, broadcastEvent]);

  // Setup session timers
  const setupTimers = useCallback(() => {
    // Clear existing timers first
    Object.values(timers.current).forEach(timer => {
      if (timer) clearTimeout(timer);
    });

    const now = Date.now();
    const { lastActivity, absoluteExpiry } = sessionState;

    const timeUntilIdleExpiry = IDLE_TIMEOUT - (now - lastActivity);
    const timeUntilAbsoluteExpiry = absoluteExpiry - now;
    const timeUntilWarning = timeUntilIdleExpiry - WARNING_BEFORE_EXPIRY;
    const timeUntilSilentRefresh = timeUntilIdleExpiry - SILENT_REFRESH_BEFORE_EXPIRY;

    // Silent refresh timer (5 minutes before idle expiry)
    if (timeUntilSilentRefresh > 0 && timeUntilSilentRefresh < timeUntilIdleExpiry) {
      timers.current.silentRefreshTimer = setTimeout(() => {
        performSilentRefresh();
      }, timeUntilSilentRefresh);
    }

    // Warning timer (5 minutes before idle expiry)
    if (timeUntilWarning > 0) {
      timers.current.warningTimer = setTimeout(() => {
        setSessionState(prev => ({ ...prev, showWarning: true }));
        broadcastEvent('SESSION_WARNING', { show: true });
      }, timeUntilWarning);
    }

    // Idle expiry timer
    if (timeUntilIdleExpiry > 0) {
      timers.current.idleTimer = setTimeout(() => {
        handleSessionExpiry('idle');
      }, timeUntilIdleExpiry);
    }

    // Absolute expiry timer
    if (timeUntilAbsoluteExpiry > 0) {
      timers.current.absoluteTimer = setTimeout(() => {
        handleSessionExpiry('absolute');
      }, timeUntilAbsoluteExpiry);
    }
  }, [sessionState, broadcastEvent, performSilentRefresh, handleSessionExpiry]);

  // Update last activity time
  const updateActivity = useCallback(
    (timestamp?: number, broadcast = true) => {
      const now = timestamp || Date.now();

      setSessionState(prev => ({
        ...prev,
        lastActivity: now,
        showWarning: false,
      }));

      if (broadcast) {
        broadcastEvent('SESSION_ACTIVITY', { timestamp: now });
      }

      // Setup new timers with updated activity
      setupTimers();
    },
    [broadcastEvent, setupTimers]
  );

  // Initialize session start time
  useEffect(() => {
    const sessionStart = localStorage.getItem('session_start');
    if (!sessionStart) {
      const now = Date.now();
      localStorage.setItem('session_start', now.toString());
      setSessionState(prev => ({
        ...prev,
        sessionStart: now,
        absoluteExpiry: now + ABSOLUTE_SESSION_CAP,
      }));
    }
  }, []);

  // Initialize BroadcastChannel for multi-tab sync
  useEffect(() => {
    if (typeof BroadcastChannel !== 'undefined') {
      broadcastChannel.current = new BroadcastChannel('kernlogic_session');

      broadcastChannel.current.onmessage = event => {
        const { type, data } = event.data;

        switch (type) {
          case 'SESSION_ACTIVITY':
            updateActivity(data.timestamp, false);
            break;
          case 'SESSION_LOGOUT':
            handleLogout(false);
            break;
          case 'SESSION_REFRESH':
            setSessionState(prev => ({ ...prev, isRefreshing: false }));
            break;
          case 'SESSION_WARNING':
            setSessionState(prev => ({ ...prev, showWarning: data.show }));
            break;
        }
      };

      return () => {
        broadcastChannel.current?.close();
      };
    }
  }, [updateActivity, handleLogout]);

  // Extend session manually
  const extendSession = useCallback(() => {
    updateActivity();
    setSessionState(prev => ({ ...prev, showWarning: false }));
    broadcastEvent('SESSION_WARNING', { show: false });
  }, [updateActivity, broadcastEvent]);

  // Activity event listeners and initial timer setup
  useEffect(() => {
    const handleActivity = () => updateActivity();

    // Add activity event listeners
    ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Initial setup of timers
    setupTimers();

    // Copy ref to avoid stale closure
    const currentTimers = timers.current;

    return () => {
      // Cleanup event listeners
      ACTIVITY_EVENTS.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });

      // Clear timers using copied ref
      Object.values(currentTimers).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
    };
  }, [updateActivity, setupTimers]);

  // Calculate time until expiry
  const now = Date.now();
  const timeUntilIdleExpiry = Math.max(0, IDLE_TIMEOUT - (now - sessionState.lastActivity));
  const timeUntilAbsoluteExpiry = Math.max(0, sessionState.absoluteExpiry - now);
  const isSessionExpired = timeUntilIdleExpiry === 0 || timeUntilAbsoluteExpiry === 0;

  return {
    sessionState,
    extendSession,
    logout: () => handleLogout(),
    isSessionExpired,
    timeUntilIdleExpiry,
    timeUntilAbsoluteExpiry,
  };
}
